/**
 * The dashboard's logic, kept apart from its rendering so it can be tested in
 * isolation — the same split projectsModel.ts and settingsModel.ts make.
 *
 * Everything here is a function of the rows and the overview: which rows are
 * asking for a person and which are getting on with it, how they are counted,
 * how long each has been quiet, what posture the pools are in, where a risk is
 * answered, and how a recommendation reads in the console's language. Nothing
 * fetches, nothing renders, nothing keeps state.
 */

import type { useT } from '@/i18n/context'
import { sessionHasEnded, type SessionActivity } from '@/lib/sessionActivity'
import type { SessionRow } from '@/terminal/wall'
import type {
  OverviewData,
  OverviewHost,
  OverviewRecommendation,
  OverviewRisk,
} from './fleet-telemetry'
import type { ID } from '@/types/domain'

export { foldForSearch, matchesQuery } from '@/lib/search'

/**
 * The order the ŞİMDİ band lists sessions in: the most actionable silence
 * first. The rank is the attention a row asks for, not the alphabet, and a
 * stable sort keeps the server's newest-first order within each band.
 */
export const BOARD_RANK: Record<SessionActivity, number> = {
  WAITING: 0,
  PROBLEM: 1,
  DONE: 2,
  WORKING: 3,
  IDLE: 4,
  UNKNOWN: 5,
  ENDED: 6,
}

/** The activities that are asking for a person, in the board's terms. */
export function needsAttention(a: SessionActivity): boolean {
  return a === 'WAITING' || a === 'PROBLEM' || a === 'DONE'
}

/**
 * The board in two lists, because it asks two questions and they have different
 * answers.
 *
 * One ranked list of everything was the previous shape, and ranking is not
 * separating: the one session that had finished its turn and was waiting to be
 * told what to do next sat first among eleven cards that looked exactly like
 * it. An operator scanning for "who needs me" had to read all eleven to find
 * out it was one. `now` is that answer on its own, and `fleet` is everything
 * that is simply getting on with it.
 *
 * Within `fleet`: working first — it is alive, and watching it is a reason to
 * be on this screen — then by how recently the agent last spoke, then the ones
 * that are over. Recency ascending rather than descending because the top of a
 * list is where the eye lands, and the agent that spoke a minute ago is the one
 * a person is most likely to be in the middle of something with. How long each
 * has been quiet is printed beside it, so the order is legible rather than
 * merely applied.
 *
 * No `heldMs`: this browser's hold time is added to every server-measured
 * silence equally, so it cannot change the order, and taking it would re-sort
 * the whole list once a second for a result identical to the one before it.
 */
export interface BoardSplit {
  /** Asking for a person, most actionable first. */
  now: SessionRow[]
  /** Everything else: working, then by how recently it spoke, then over. */
  fleet: SessionRow[]
}

export function splitBoard(
  rows: SessionRow[],
  activityOf: (s: SessionRow) => SessionActivity,
): BoardSplit {
  const now: { s: SessionRow; i: number; rank: number }[] = []
  const fleet: { s: SessionRow; i: number; rank: number; quiet: number }[] = []

  rows.forEach((s, i) => {
    const a = activityOf(s)
    if (needsAttention(a)) {
      now.push({ s, i, rank: BOARD_RANK[a] })
      return
    }
    fleet.push({
      s,
      i,
      rank: a === 'WORKING' ? 0 : a === 'ENDED' ? 2 : 1,
      // Unmeasured sorts after everything measured rather than at either end.
      // "Nothing has ever seen this one speak" is not "it spoke just now", and
      // it is not "it has been silent longest" either — it is not on the scale.
      quiet: silenceOf(s)?.ms ?? Number.POSITIVE_INFINITY,
    })
  })

  // Written out rather than subtracted: two unmeasured rows would compare
  // `Infinity - Infinity`, which is NaN, and a comparator that returns NaN has
  // no defined result.
  const cmp = (x: number, y: number) => (x === y ? 0 : x < y ? -1 : 1)

  now.sort((a, b) => a.rank - b.rank || a.i - b.i)
  fleet.sort((a, b) => a.rank - b.rank || cmp(a.quiet, b.quiet) || a.i - b.i)
  return { now: now.map((r) => r.s), fleet: fleet.map((r) => r.s) }
}

/** How many rows wear each light — counted from the same derivation the rows
 *  use, so a tile and the rows under it cannot disagree. */
export function summariseBoard(
  rows: SessionRow[],
  activityOf: (s: SessionRow) => SessionActivity,
): Record<SessionActivity, number> {
  const counts: Record<SessionActivity, number> = {
    WORKING: 0,
    WAITING: 0,
    DONE: 0,
    IDLE: 0,
    ENDED: 0,
    PROBLEM: 0,
    UNKNOWN: 0,
  }
  for (const s of rows) counts[activityOf(s)]++
  return counts
}

/* Two things were deleted from this file, and both are worth saying why.
 *
 * **The activity filter.** `passesBoardFilter` backed a row of chips — Tümü /
 * Tur bitti / Çalışıyor / Boşta — above a single ranked list. Splitting the
 * board into ŞİMDİ and FİLO *is* that filter, applied always and for free, and
 * it is the only cut of the eleven anybody was seen to make. What the chips
 * added on top was a second control saying the same thing, on the same line as
 * the search field and competing with it for the width.
 *
 * **The project rollup.** `projectRollup`, `lastMovedAt` and `ProjectRoll` were
 * written, typed and covered by tests, and no component ever imported any of
 * them — this codebase's most persistent defect (see `kimene-doc-audit`). They
 * were kept through the first redesign on the theory that the board would grow
 * a project band, and it did not: the fleet runs one long-lived agent per
 * project, so a rollup is the same list with the project name moved from the
 * row to a heading above it. `passesBoardFilter`'s unused `projectId` parameter
 * was the same bet, and went with it.
 *
 * If either is wanted again, the thing to add is a control over `fleet` — a
 * filter, or a grouping — not a second list beside it. */

/* ------------------------------------------------------------------ Silence */

/**
 * How long an agent has been quiet, and whether its silence means anything.
 *
 * The board's time fact was the session's **age** — time since it started — and
 * on this fleet that is a number with no decision in it: eleven sessions started
 * within a few hours of each other, so the column read 60sa, 61sa, 62sa, 63sa
 * down the screen and told an operator nothing. The number that changes what
 * they do is the other one: how long since this agent last did anything.
 *
 * The sources are ranked the way `sessionActivity` ranks them, and for the same
 * reason — each is better evidence than the one under it:
 *
 *   1. `doneAgoMs` — the agent said its turn was over. It has been quiet since
 *      then *deliberately*, and it is waiting to be told what is next.
 *   2. `waitingAgoMs` — the agent stopped for a person. Quiet because it is
 *      asking, which is the most actionable silence there is.
 *   3. `busyAgoMs` — last seen producing output. A proxy, but a measured one.
 *   4. the transcript's own timestamps, for a session this control plane holds
 *      no terminal view of. Weakest, and the only one that survives a restart.
 *
 * All three `*AgoMs` fields are measured **on the server when the payload was
 * made**, so the browser's own hold time is added rather than a server clock
 * being compared with a laptop's — the same arithmetic `useSessionActivity`
 * does, and for the same reason (see `sessionActivity`'s note on `staleMs`).
 *
 * Absent is absent. A session nothing has measured has no silence to report, and
 * this returns `undefined` rather than a zero that would read as "just now".
 */
/**
 * The sessions whose waiting signal cannot be trusted — when nothing can be.
 *
 * A session that claims `reportsTurns` was launched with the hook that reports
 * "this agent has stopped for a person". `signalAgoMs` is that hook having
 * actually run. The two came apart on this deployment for nine days: every pane
 * carried a `$TMUX` naming a socket that had been renamed away, so every write
 * the hook made failed — silently, because a hook that fails loudly interrupts
 * the prompt it exists to report. Seven live agents, and a board that called a
 * session sitting at a permission prompt idle. Nothing on any screen could have
 * said so, because nothing held the difference between "not waiting" and "never
 * heard from".
 *
 * The test is deliberately **fleet-wide, not per session**. A single agent that
 * has not spoken is the ordinary case — it is between turns, and the hook has
 * had no occasion to run; saying so would put a permanent count on the board
 * that an operator can do nothing with, which is decoration. *Nobody* having
 * ever spoken, while agents are demonstrably running, is not an ordinary case:
 * it is the channel being down, and it is the exact signature of the outage
 * above. One session reporting clears it for all of them, because one is proof
 * the channel works.
 *
 * The blind spot is deliberate and worth stating: a single broken hook among
 * healthy ones is not reported. The hook is written per workspace from one
 * template, so channel-wide is how it fails; and the alternative was an alarm
 * that is on every day, which is an alarm nobody reads.
 *
 * `busyAgoMs` is required so a fleet that has only just started — hooks
 * installed, nothing run yet — is not accused of an outage. OBSERVED sessions
 * are excluded (Kimene installed nothing for them and reports that elsewhere)
 * and so are ended ones (nothing is waiting on a person there).
 */
export function unprovenSignal(rows: SessionRow[]): SessionRow[] {
  const claimed = rows.filter(
    (s) => s.reportsTurns === true && s.kind !== 'OBSERVED' && !sessionHasEnded(s.state),
  )
  if (claimed.some((s) => s.signalAgoMs != null)) return []
  return claimed.filter((s) => s.busyAgoMs != null)
}

export interface Silence {
  /** Milliseconds since the agent was last seen doing anything. */
  ms: number
  /**
   * Which signal the measurement came from, so the interface can say what the
   * number is worth rather than presenting four different measurements as one.
   *
   * Deliberately *not* `reportsTurns`. That field is the obvious candidate for
   * "why is it quiet", and it cannot answer: the server sends it only when it is
   * true, so absence means "cannot report" and "was not asked" at once. Saying
   * "no turn signal" on absence would be asserting a negative from missing data,
   * which is the one move this console does not make.
   */
  from: 'done' | 'waiting' | 'busy' | 'transcript'
}

export function silenceOf(row: SessionRow, staleMs = 0): Silence | undefined {
  const held = Math.max(0, staleMs)
  const measured = (ms: number | undefined, from: Silence['from']): Silence | undefined =>
    ms == null || Number.isNaN(ms) ? undefined : { ms: ms + held, from }

  const server =
    measured(row.doneAgoMs, 'done') ??
    measured(row.waitingAgoMs, 'waiting') ??
    measured(row.busyAgoMs, 'busy')
  if (server) return server

  // The transcript, when the terminal subsystem has no view. This one is an
  // absolute timestamp rather than an age, so it is differenced against the
  // browser's clock — which is the best available and is why it ranks last.
  const at = row.lastTurn?.replyAt ?? row.lastTurn?.promptAt ?? row.lastTurn?.observedAt
  if (!at) return undefined
  const ms = Date.now() - new Date(at).getTime()
  return Number.isNaN(ms) ? undefined : { ms: Math.max(0, ms), from: 'transcript' }
}

/**
 * Whether the card should report silence at all.
 *
 * Not while it is working — an agent producing output right now is not quiet,
 * and "3sn sessiz" beside a live spinner is a sentence that argues with the dot
 * next to it. Not once it has ended either: `SessionEnded` already says when and
 * how it finished, and two times on one card is one time too many.
 *
 * There is deliberately **no threshold**. The obvious design was to colour a card
 * past some age, and on this fleet that paints nine of eleven the same amber —
 * a wall that means nothing, which is the state the board was already in.
 * CLAUDE.md's rule is that attention is spent, not decorated: past a threshold a
 * card would offer exactly the control it already offers at rest, so the
 * threshold would be buying emphasis and nothing else. The duration is stated
 * precisely instead, in mono, and 26dk and 3g are told apart by reading them.
 */
export function reportsSilence(row: SessionRow, activity: SessionActivity): boolean {
  return activity !== 'WORKING' && !row.endedAt
}

/* --------------------------------------------------------- The pool's posture */

/**
 * Can the workforce keep working?
 *
 * Two readings, and neither of them is on the top bar. The bar carries the
 * *tightest quota* — the one provider figure that decides whether the next hour
 * is possible — and this is the other question: how many accounts the router
 * would actually accept work on right now, and how many machines are answering.
 * A subscription with headroom on a profile that is disabled, unconfigured or
 * awaiting a login is not capacity, and `canAcceptWork` is the router's own
 * verdict rather than a second opinion derived here.
 *
 * `ready` and `online` are counts of a *known* condition. An account the server
 * sent no verdict for is not counted as ready, and a host with no status is not
 * counted as online — the denominator says how many there are, so "5/7" cannot
 * be read as "all of them".
 */
export interface PoolPosture {
  accountsReady: number
  accountsTotal: number
  hostsOnline: number
  hostsTotal: number
  /** Online, but not to be relied on: degraded, or not heard from in a while.
   *  Counted apart from the offline ones because the two are different jobs. */
  hostsDegraded: number
}

export function summarisePools(overview: OverviewData | undefined): PoolPosture {
  const accounts = overview?.accounts ?? []
  const hosts = overview?.hosts ?? []
  return {
    accountsReady: accounts.filter((a) => a.canAcceptWork === true).length,
    accountsTotal: accounts.length,
    hostsOnline: hosts.filter((h) => h.status === 'ONLINE').length,
    hostsTotal: hosts.length,
    hostsDegraded: hosts.filter((h: OverviewHost) => h.status === 'DEGRADED').length,
  }
}

/**
 * Where a risk is answered.
 *
 * The top bar's risk count links here, so this screen owes every risk it lists a
 * way to act on it — a sentence with nowhere to go is the decoration CLAUDE.md
 * refuses. The server already says what each risk is *about* (`targetType` and
 * `targetId`), so the destination is read off the risk rather than guessed from
 * its kind: a detector added after this build lands on the right screen without
 * this function knowing its name.
 *
 * A dependency failure is the one that returns nothing on purpose. There is no
 * console screen that restarts MongoDB, and a link to somewhere unhelpful is
 * worse than a plain statement of fact.
 */
export function riskHref(risk: OverviewRisk): string | undefined {
  if (!risk.targetId) return undefined
  switch (risk.targetType) {
    case 'host':
      return `/hosts/${risk.targetId}`
    case 'account':
      return '/accounts'
    case 'task':
      return `/tasks/${risk.targetId}`
    case 'session':
      return `/live/${risk.targetId}`
    default:
      return undefined
  }
}

/** The reason a recommendation gives for an account being unusable, in the
 *  console's language; the server's word for a kind it does not know. */
function reasonWord(reason: string | undefined, t: ReturnType<typeof useT>): string {
  switch (reason) {
    case 'exhausted':
      return t('advice.reason.exhausted')
    case 'auth_required':
      return t('advice.reason.auth_required')
    case 'disabled':
      return t('advice.reason.disabled')
    default:
      return reason ?? ''
  }
}

/**
 * A recommendation as a sentence in the console's language, composed from its
 * kind and params; the server's English title for a kind this build does not
 * know — a rule added after the console was built must still say something.
 */
export function adviceSentence(rec: OverviewRecommendation, t: ReturnType<typeof useT>): string {
  const p = rec.params ?? {}
  switch (rec.kind) {
    case 'answer_waiting_session':
      return t('advice.answer_waiting_session', { session: p.session ?? '', minutes: p.minutes ?? '' })
    case 'read_finished_turn':
      return t('advice.read_finished_turn', { session: p.session ?? '', minutes: p.minutes ?? '' })
    case 'switch_session_account':
      return t('advice.switch_session_account', {
        session: p.session ?? '',
        fromAccount: p.fromAccount ?? '',
        toAccount: p.toAccount ?? '',
        reason: reasonWord(p.reason, t),
      })
    case 'session_account_blocked':
      return t('advice.session_account_blocked', {
        session: p.session ?? '',
        fromAccount: p.fromAccount ?? '',
        reason: reasonWord(p.reason, t),
      })
    case 'restart_failed_session':
      return p.exit
        ? t('advice.restart_failed_session.exit', { session: p.session ?? '', exit: p.exit })
        : t('advice.restart_failed_session', { session: p.session ?? '' })
    case 'check_quiet_session':
      return t('advice.check_quiet_session', { session: p.session ?? '', minutes: p.minutes ?? '' })
    case 'tool_errors_in_turn':
      return t('advice.tool_errors_in_turn', { session: p.session ?? '', errors: p.errors ?? '' })
    case 'idle_capacity':
      return t('advice.idle_capacity', { count: p.count ?? '', accounts: p.accounts ?? '' })
    case 'decide_on_task':
      return t('advice.decide_on_task', { task: p.task ?? '', state: p.state ?? '' })
    case 'start_ready_task':
      return t('advice.start_ready_task', { task: p.task ?? '' })
    case 'task_session_ended':
      return t('advice.task_session_ended', { task: p.task ?? '' })
    default:
      return rec.title
  }
}

export type AdviceActionKind =
  | 'open_session'
  | 'restart_session'
  | 'switch_account'
  | 'start_session'
  | 'open_task'
  | 'navigate'

/** What control a recommendation's action earns. Unknown kinds become a plain
 *  link to the path the server gave, so an action this build has never heard of
 *  still has somewhere to go. */
export function adviceActionKind(rec: OverviewRecommendation): AdviceActionKind {
  switch (rec.action.kind) {
    case 'open_session':
    case 'restart_session':
    case 'switch_account':
    case 'start_session':
    case 'open_task':
      return rec.action.kind
    default:
      return 'navigate'
  }
}

/** The session a recommendation is about, when it is about one. */
export function adviceSessionId(rec: OverviewRecommendation): ID | undefined {
  return rec.targetType === 'session' ? rec.targetId : undefined
}
